import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { docsCharacterOffersApi } from '../api/docsCharacterOffers';
import { pendingOffersApi } from '../api/pendingOffers';
import DocsStoryOrderOffers from '../components/DocumentImport/DocsStoryOrderOffers';
import UploadPathOffers from '../components/DocumentImport/UploadPathOffers';
import { usePaidConfirm } from '../components/Common/usePaidConfirm';
import { projectsApi } from '../api/projects';
import { storiesApi } from '../api/stories';
import {
  KIND_HEADING,
  REVIEW_GROUP_ORDER,
  defaultDisposition,
  destinationNote,
  doorAltitude,
  emptyReasonCopy,
  existingNotesCopy,
  groupCandidates,
  isBookOnlyKind,
  loadDocsOfferSources,
  needsAltitudePicker,
  offerCardTitle,
  offerKind,
  persistAcceptedDocsNote,
  pickExistingTarget,
  resolveAcceptAltitude,
  selectedIds,
  singletonDefaultTitle,
} from '../utils/docsCharacterOffers';
import { bookNotesPath, projectNotesPath } from '../utils/seriesNotes';
import { seriesCardsPath } from '../utils/seriesSnapshots';
import { storyRulesPath } from '../utils/storyRulesPath';
import { styleCardsPath } from '../utils/styleCardsPath';
import {
  ACCEPT_NOTE_ERROR,
  BACK_TO_DOCS,
  DOCS_CHARACTER_PASS_DONE,
  DOCS_EMPTY,
  FROM_DOCS_LEDE,
  MAKE_NEW_NOTE,
  MAKE_NOTES_FROM_DOCS,
  MAKE_NOTES_FROM_THESE,
  NO_MATCH_IN_DOCS,
  OPEN_BOOK_NOTES,
  OPEN_SERIES_NOTES,
  OPEN_STORY_RULES,
  OPEN_STYLE_NOTES,
  GIVE_VOICE_ITS_TEXT,
  STYLE_NOTES,
  VOICE_ALREADY_HERE,
  VOICE_DRAFT,
  VOICE_NAME,
  WHERE_VOICE_LANDS,
  PICK_DOCS_FIRST,
  PROPOSE_DOCS_CHARACTER_ERROR,
  REVIEW_DOCS_CHARACTER_LEDE,
  SERIES_ALTITUDE,
  THIS_BOOK_ALTITUDE,
  UPDATE_EXISTING_NOTE,
} from '../utils/writerCopy';
import '../components/BookSheet/BookSheet.css';
import './SeriesOffersPage.css';

/**
 * Docs room Make-notes door (#1037 / #1044 / #1046 / #1049).
 * Character, Place, World, spine, Story Rules, and voice.
 * Same Accept / edit / Skip checklist.
 */
export default function DocsCharacterOffersPage() {
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();
  const { projectId, storyId } = useParams();
  const [searchParams] = useSearchParams();
  const { currentProject, currentStory, setCurrentProject, setCurrentStory } = useApp();
  const preselectedBatch = searchParams.get('batch');

  const [project, setProject] = useState(
    currentProject?.id === projectId ? currentProject : null,
  );
  const [docs, setDocs] = useState([]);
  const [books, setBooks] = useState([]);
  const [checked, setChecked] = useState({});
  const [step, setStep] = useState('checklist');
  const [candidates, setCandidates] = useState([]);
  const [emptyReason, setEmptyReason] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [done, setDone] = useState({});
  const [loading, setLoading] = useState(true);
  const [proposing, setProposing] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);

  const isSeries = Boolean(project?.is_series);
  const door = doorAltitude({ storyId, isSeries });
  const docsRoomPath = storyId
    ? bookNotesPath(projectId, storyId)
    : projectNotesPath(projectId);

  useEffect(() => {
    if (storyId) return undefined;
    if (isSeries) setCurrentStory(null);
    return undefined;
  }, [storyId, isSeries, setCurrentStory]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!projectId) return;
      if (currentProject?.id === projectId) {
        setProject(currentProject);
        return;
      }
      try {
        const next = await projectsApi.get(projectId);
        if (!cancelled && next) {
          setProject(next);
          setCurrentProject(next);
        }
      } catch {
        if (!cancelled) setProject(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId, currentProject, setCurrentProject]);

  useEffect(() => {
    if (!storyId || currentStory?.id === storyId) return undefined;
    let cancelled = false;
    storiesApi.get(storyId).then((story) => {
      if (!cancelled && story) setCurrentStory(story);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [storyId, currentStory, setCurrentStory]);

  const loadSources = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const checklist = await loadDocsOfferSources(projectId);
      setDocs(checklist.docs);
      const nextChecked = {};
      checklist.docs.forEach((item) => {
        nextChecked[item.id] = preselectedBatch
          ? item.id === preselectedBatch
          : true;
      });
      setChecked(nextChecked);
      setError(checklist.error ? (checklist.error.message || 'Could not load docs.') : null);
    } catch (err) {
      setError(err.message || 'Could not load docs.');
    } finally {
      setLoading(false);
    }
  }, [projectId, preselectedBatch]);

  useEffect(() => { loadSources(); }, [loadSources]);

  const batchIds = useMemo(() => selectedIds(checked, docs), [checked, docs]);
  const showPickDocsHint = docs.length > 0 && batchIds.length === 0;
  const groups = useMemo(() => groupCandidates(candidates), [candidates]);
  const remaining = candidates.filter((row) => !done[row.id]);

  const toggle = (id) => {
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const propose = async () => {
    if (!batchIds.length) {
      setError(PICK_DOCS_FIRST);
      return;
    }
    const ok = await askPaid('docs_notes');
    if (!ok) return;
    setProposing(true);
    try {
      const result = await docsCharacterOffersApi.propose(projectId, {
        batchIds,
        storyId: storyId || null,
        defaultAltitude: door,
      });
      const next = result?.candidates || [];
      setCandidates(next);
      if (storyId && next.length) {
        try {
          await pendingOffersApi.remember(storyId, next.map((row) => ({
            id: row.id,
            title: row.title,
            detail: row.statement,
            offer_kind: row.kind,
          })));
        } catch {
          // The Docs page still shows the offers. Notes picks them up
          // once the pending list can store them.
        }
      }
      setEmptyReason(result?.empty_reason || (next.length ? null : 'no_match'));
      setBooks(result?.books || []);
      const nextDrafts = {};
      next.forEach((row) => {
        const kind = offerKind(row.kind);
        const suggested = isBookOnlyKind(kind) ? 'book' : (row.suggested_altitude || door);
        nextDrafts[row.id] = {
          title: String(row.title || row.name || '').trim(),
          statement: row.statement,
          altitude: suggested,
          disposition: defaultDisposition(row),
          storyId: storyId || row.collision?.story_id || (result?.books?.length === 1 ? result.books[0].id : ''),
        };
      });
      setDrafts(nextDrafts);
      setDone({});
      setStep('review');
      setError(null);
    } catch (err) {
      setError(err.message || PROPOSE_DOCS_CHARACTER_ERROR);
    } finally {
      setProposing(false);
    }
  };

  const editDraft = (id, field, value) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const skip = (id) => {
    setDone((prev) => ({ ...prev, [id]: 'skipped' }));
    if (!storyId) return;
    pendingOffersApi.reject(storyId, { kind: 'docs', source_ref: id }).catch(() => {});
  };

  const accept = async (candidate) => {
    const draft = drafts[candidate.id] || {};
    const kind = offerKind(candidate.kind);
    const title = (draft.title || '').trim() || singletonDefaultTitle(kind);
    const statement = (draft.statement || '').trim();
    const altitude = isBookOnlyKind(kind)
      ? 'book'
      : resolveAcceptAltitude(candidate, draft.altitude, {
        isSeries,
        doorAltitude: door,
      });
    const disposition = draft.disposition || defaultDisposition(candidate);
    const existingTarget = pickExistingTarget(candidate, disposition);
    const acceptStoryId = altitude === 'book'
      ? (draft.storyId || existingTarget?.story_id || storyId || null)
      : null;
    if (!statement || (!singletonDefaultTitle(kind) && !title)) {
      setError(
        kind === 'place'
          ? 'Give this Place note a name and what the place is before you accept it.'
          : kind === 'world'
            ? 'Give this World note its facts before you accept it.'
            : kind === 'logline'
              ? 'Give this Logline note its text before you accept it.'
              : kind === 'theme'
                ? 'Give this Theme note its text before you accept it.'
                : kind === 'conflict'
                  ? 'Give this Conflict note its text before you accept it.'
                  : kind === 'relationship'
                    ? 'Give this Relationship note a name and what it is before you accept it.'
                    : kind === 'subplot'
                      ? 'Give this Subplot note a name and what it is before you accept it.'
                      : kind === 'custom'
                        ? 'Give this Custom note a title and its text before you accept it.'
                        : kind === 'rule'
                          ? 'Give this Story Rule the hard rule that must stay true before you accept it.'
                          : kind === 'voice'
                            ? GIVE_VOICE_ITS_TEXT
                            : 'Give this Character note a name and a portrait before you accept it.',
      );
      return;
    }
    setSavingId(candidate.id);
    try {
      await persistAcceptedDocsNote({
        projectId,
        storyId: acceptStoryId,
        altitude,
        disposition,
        candidate,
        kind,
        title,
        statement,
        existingTarget,
      });
      setDone((prev) => ({ ...prev, [candidate.id]: 'accepted' }));
      if (storyId) {
        pendingOffersApi.reject(storyId, { kind: 'docs', source_ref: candidate.id }).catch(() => {});
      }
      setError(null);
    } catch (err) {
      setError(err.message || ACCEPT_NOTE_ERROR);
    } finally {
      setSavingId(null);
    }
  };

  const renderCandidate = (candidate) => {
    const draft = drafts[candidate.id] || candidate;
    const status = done[candidate.id];
    if (status) {
      return (
        <li key={candidate.id} className="so-card so-card--done">
          <p>{status === 'accepted' ? 'Accepted.' : 'Skipped.'} {candidate.title}</p>
        </li>
      );
    }
    const kind = offerKind(candidate.kind);
    const altitude = isBookOnlyKind(kind)
      ? 'book'
      : (draft.altitude || candidate.suggested_altitude || door);
    const showAltitude = needsAltitudePicker(candidate, { isSeries, doorAltitude: door });
    const showBookPicker = isSeries && (altitude === 'book' || isBookOnlyKind(kind)) && !storyId && books.length > 0;
    const defaultTitle = singletonDefaultTitle(kind);
    const cardTitle = offerCardTitle(candidate, draft) || defaultTitle;
    const nameLabel = kind === 'custom' ? 'Title' : (kind === 'voice' ? VOICE_NAME : (defaultTitle || 'Name'));
    const bodyLabel = kind === 'place'
      ? 'What this place is'
      : kind === 'world'
        ? 'Facts and rules'
        : kind === 'people'
          ? 'Portrait'
          : kind === 'relationship'
            ? 'The relationship'
            : kind === 'subplot'
              ? 'The main storyline'
              : kind === 'custom'
                ? 'Note'
                : kind === 'rule'
                  ? 'Hard rule that must stay true'
                  : kind === 'voice'
                    ? VOICE_DRAFT
                    : KIND_HEADING[kind] || 'Note';
    const landLabel = kind === 'place'
      ? 'Where this Place note lands'
      : kind === 'world'
        ? 'Where this World note lands'
        : kind === 'custom'
          ? 'Where this Custom note lands'
          : kind === 'rule'
            ? 'Where this Story Rule lands'
            : kind === 'voice'
              ? WHERE_VOICE_LANDS
              : `Where this ${KIND_HEADING[kind] || 'note'} lands`;
    return (
      <li key={candidate.id} className="so-card">
        {cardTitle ? <h3 className="so-card__title">{cardTitle}</h3> : null}
        <label className="so-field">
          <span>{nameLabel}</span>
          <input
            value={draft.title || defaultTitle}
            onChange={(e) => editDraft(candidate.id, 'title', e.target.value)}
          />
        </label>
        <label className="so-field">
          <span>{bodyLabel}</span>
          <textarea
            rows={5}
            value={draft.statement || ''}
            onChange={(e) => editDraft(candidate.id, 'statement', e.target.value)}
          />
        </label>
        <p className="so-dest">{destinationNote(altitude, kind)}</p>
        {showAltitude && (
          <fieldset className="so-field">
            <legend>{landLabel}</legend>
            <label className="so-check">
              <input
                type="radio"
                name={`alt-${candidate.id}`}
                checked={altitude === 'book'}
                onChange={() => editDraft(candidate.id, 'altitude', 'book')}
              />
              <span>{THIS_BOOK_ALTITUDE}</span>
            </label>
            <label className="so-check">
              <input
                type="radio"
                name={`alt-${candidate.id}`}
                checked={altitude === 'series'}
                onChange={() => editDraft(candidate.id, 'altitude', 'series')}
              />
              <span>{SERIES_ALTITUDE}</span>
            </label>
          </fieldset>
        )}
        {showBookPicker && (
          <label className="so-field">
            <span>Book</span>
            <select
              value={draft.storyId || ''}
              onChange={(e) => editDraft(candidate.id, 'storyId', e.target.value)}
            >
              <option value="">Pick a book…</option>
              {books.map((book) => (
                <option key={book.id} value={book.id}>{book.name}</option>
              ))}
            </select>
          </label>
        )}
        {candidate.existing?.length > 0 && (
          <div className="so-existing">
            <p>{existingNotesCopy(kind, candidate.collision)}</p>
            <ul>
              {candidate.existing.map((row) => (
                <li key={`${row.store}-${row.id}`}>
                  {row.name}
                  {row.story_name ? ` · ${row.story_name}` : row.store === 'snapshots' ? ' · Series Notes' : row.store === 'rules' ? ' · Story Rules' : row.store === 'style' ? ` · ${STYLE_NOTES}` : ''}
                  {': '}
                  {row.description || 'No line yet.'}
                </li>
              ))}
            </ul>
            {candidate.collision && (
              <fieldset className="so-field">
                <legend>
                  {kind === 'rule'
                    ? 'This hard rule is already in Story Rules'
                    : kind === 'voice'
                      ? VOICE_ALREADY_HERE
                      : 'This name is already in Notes'}
                </legend>
                <label className="so-check">
                  <input
                    type="radio"
                    name={`disp-${candidate.id}`}
                    checked={(draft.disposition || defaultDisposition(candidate)) === 'update'}
                    onChange={() => editDraft(candidate.id, 'disposition', 'update')}
                  />
                  <span>{UPDATE_EXISTING_NOTE}</span>
                </label>
                <label className="so-check">
                  <input
                    type="radio"
                    name={`disp-${candidate.id}`}
                    checked={(draft.disposition || defaultDisposition(candidate)) === 'new'}
                    onChange={() => editDraft(candidate.id, 'disposition', 'new')}
                  />
                  <span>{MAKE_NEW_NOTE}</span>
                </label>
              </fieldset>
            )}
          </div>
        )}
        <div className="so-actions">
          <button
            type="button"
            className="bs-btn bs-btn--primary bs-btn--small"
            onClick={() => accept(candidate)}
            disabled={savingId === candidate.id}
          >
            Accept
          </button>
          <button
            type="button"
            className="bs-btn bs-btn--secondary bs-btn--small"
            onClick={() => skip(candidate.id)}
            disabled={savingId === candidate.id}
          >
            Skip
          </button>
        </div>
      </li>
    );
  };

  if (loading) return <div className="bs-panel"><p>Loading…</p></div>;

  const notesAfter = door === 'series'
    ? seriesCardsPath(projectId)
    : '/bibles';
  const acceptedKinds = new Set(
    candidates.filter((row) => done[row.id] === 'accepted').map((row) => offerKind(row.kind)),
  );
  const afterStoryId = storyId || books[0]?.id || currentStory?.id || null;

  return (
    <div className="bs-panel so-page">
      {paidDialog}
      <div className="bs-header">
        <div>
          <h1>{MAKE_NOTES_FROM_DOCS}</h1>
          <p className="bs-header__subtitle">{project?.name || ''}</p>
        </div>
        <Link className="bs-add-link" to={docsRoomPath}>
          {BACK_TO_DOCS}
        </Link>
      </div>

      {error && (
        <div className="bs-error">
          {error}{' '}
          <button type="button" className="bs-btn--danger" onClick={() => setError(null)}>dismiss</button>
        </div>
      )}

      {step === 'checklist' && (
        <>
          <p className="so-lede">{FROM_DOCS_LEDE}</p>
          <section className="so-section">
            <h2>Docs</h2>
            {docs.length === 0 && <p className="so-empty">{DOCS_EMPTY}</p>}
            <ul className="so-list">
              {docs.map((item) => (
                <li key={item.id}>
                  <label className="so-check">
                    <input
                      type="checkbox"
                      checked={Boolean(checked[item.id])}
                      onChange={() => toggle(item.id)}
                    />
                    <span>
                      <strong>{item.label}</strong>
                      <em>{item.detail}</em>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </section>
          <button
            type="button"
            className="bs-btn bs-btn--primary"
            onClick={propose}
            disabled={proposing || batchIds.length === 0}
            aria-describedby={showPickDocsHint ? 'pick-docs-hint' : undefined}
          >
            {proposing ? 'Proposing…' : MAKE_NOTES_FROM_THESE}
          </button>
          {showPickDocsHint && (
            <p className="so-hint" id="pick-docs-hint">{PICK_DOCS_FIRST}</p>
          )}
          <DocsStoryOrderOffers
            projectId={projectId}
            storyId={storyId || null}
            batchIds={batchIds}
          />
          <UploadPathOffers
            projectId={projectId}
            storyId={storyId || null}
            batchIds={batchIds}
          />
        </>
      )}

      {step === 'review' && (
        <>
          <p className="so-lede">{REVIEW_DOCS_CHARACTER_LEDE}</p>
          {candidates.length === 0 && (
            <p className="so-lede">{emptyReasonCopy(emptyReason) || NO_MATCH_IN_DOCS}</p>
          )}
          {remaining.length === 0 && candidates.length > 0 && (
            <p className="so-lede">
              {DOCS_CHARACTER_PASS_DONE}
              {' '}
              <Link to={notesAfter}>{door === 'series' ? OPEN_SERIES_NOTES : OPEN_BOOK_NOTES}</Link>
              {acceptedKinds.has('rule') && afterStoryId && (
                <>
                  {' · '}
                  <Link to={storyRulesPath(afterStoryId)}>{OPEN_STORY_RULES}</Link>
                </>
              )}
              {acceptedKinds.has('voice') && afterStoryId && (
                <>
                  {' · '}
                  <Link to={styleCardsPath(afterStoryId)}>{OPEN_STYLE_NOTES}</Link>
                </>
              )}
              {' · '}
              <Link to={docsRoomPath}>Open Docs</Link>
            </p>
          )}
          {REVIEW_GROUP_ORDER.map((kind) => {
            const rows = groups[kind];
            if (!rows.length) return null;
            return (
              <section key={kind} className="so-section">
                <h2>{KIND_HEADING[kind]}</h2>
                <ul className="so-cards">{rows.map(renderCandidate)}</ul>
              </section>
            );
          })}
          <button type="button" className="bs-btn bs-btn--secondary" onClick={() => setStep('checklist')}>
            Back to Docs
          </button>
        </>
      )}
    </div>
  );
}
